// mui
import { alpha, Box, type SxProps, type Theme } from '@mui/material';

// components
import { Icon } from '@/components/atoms/Icon/Icon';

export type KeniOrbState = 'idle' | 'listening' | 'speaking';

export interface KeniOrbProps {
  /** Disc diameter in px. */
  size?: number;
  state?: KeniOrbState;
}

/** A circle filling its parent. */
// Below 1, so what scrolls behind the orb still shows faintly.
const OUTER_DISC_OPACITY = 0.85;

const circle: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
};

const keyframes: SxProps<Theme> = {
  '@keyframes keniRipple': {
    '0%': { transform: 'scale(0.55)', opacity: 0.6 },
    '100%': { transform: 'scale(2.3)', opacity: 0 },
  },
  '@keyframes keniBreatheSpeak': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.11)' },
  },
  '@keyframes keniBreatheCalm': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.04)' },
  },
};

const RING_DURATION_S = 4;

/** Breathing animation and ring count per state. */
const MOTION: Record<
  KeniOrbState,
  { breathe: string; durationS: number; rings: number }
> = {
  idle: { breathe: 'keniBreatheCalm', durationS: 5.5, rings: 0 },
  listening: { breathe: 'keniBreatheCalm', durationS: 4.5, rings: 2 },
  speaking: { breathe: 'keniBreatheSpeak', durationS: 1.4, rings: 0 },
};

/** As fractions of `size`. */
const INNER_DISC_INSET = 0.16;
const ICON_WELL_INSET = 0.34;
const ICON_SIZE = 0.26;

const Ring = ({ delayS }: { delayS: number }) => (
  <Box
    sx={{
      ...circle,
      border: 2,
      borderColor: 'custom.coral.500',
      animation: `keniRipple ${RING_DURATION_S}s ease-out infinite`,
      animationDelay: `${delayS}s`,
    }}
  />
);

/** KeniOrb is the animated coral disc used for voice input. */
export const KeniOrb = ({ size = 96, state = 'idle' }: KeniOrbProps) => {
  const { breathe, durationS, rings } = MOTION[state];

  return (
    <Box
      sx={{
        ...keyframes,
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        pointerEvents: 'none',
      }}
    >
      {Array.from({ length: rings }, (_, i) => (
        <Ring key={i} delayS={(RING_DURATION_S / rings) * i} />
      ))}

      <Box
        sx={{
          ...circle,
          bgcolor: (theme) =>
            alpha(theme.palette.custom.coral[600], OUTER_DISC_OPACITY),
          animation: `${breathe} ${durationS}s ease-in-out infinite`,
        }}
      >
        <Box
          sx={{
            ...circle,
            inset: size * INNER_DISC_INSET,
            bgcolor: 'custom.coral.500',
          }}
        />
        <Box
          sx={{
            ...circle,
            inset: size * ICON_WELL_INSET,
            display: 'grid',
            placeItems: 'center',
            color: 'custom.neutral.50',
          }}
        >
          <Icon
            icon={state === 'listening' ? 'stop' : 'microphone'}
            type='fas'
            fontSize={Math.round(size * ICON_SIZE)}
          />
        </Box>
      </Box>
    </Box>
  );
};

KeniOrb.displayName = 'KeniOrb';
export default KeniOrb;
